import React, { useEffect, useRef, useState } from 'react';
import { MessageCircleQuestion, Loader2, Send } from 'lucide-react';
import { api } from './api';
import { errorMessage } from './errors';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';

const STARTERS = {
  contact: [
    'What is the latest on this client?',
    'What did they last say to us?',
    'Which of their loans is furthest along?',
  ],
  deal: [
    'Where is this loan right now?',
    'How many conditions are still open?',
    'Who is on this deal?',
  ],
};

// "Ask Audrey" in the header of a contact or deal page. `kind` is "contact" or "deal".
// The backend shows Audrey the same fields as Summarize (never the whole record),
// refuses a record the caller cannot open, and turns her [type:id] tags into the
// numbered Sources listed under each answer. The thread lives here only; closing the
// pane keeps it until the page changes.
export default function AskAudreyButton({ kind, id, className = '' }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const convo = useRef(0);

  // A different contact or deal is a different conversation.
  useEffect(() => {
    convo.current += 1;
    setThread([]);
    setLoading(false);
    setQuestion('');
    setError('');
  }, [kind, id]);

  useEffect(() => {
    if (endRef.current && endRef.current.scrollIntoView) endRef.current.scrollIntoView({ block: 'end' });
  }, [thread, loading, error]);

  const ask = async (text) => {
    const q = String(text || '').trim();
    if (!q || loading) return;
    const mine = convo.current;
    setLoading(true);
    setError('');
    setQuestion('');
    setThread((t) => [...t, { role: 'user', text: q }]);
    try {
      const { data } = await api.post('/v2/ai/qa', {
        question: q,
        ...(kind === 'deal' ? { deal_id: id } : { contact_id: id }),
      });
      if (mine !== convo.current) return; // the page moved to another contact or deal
      setThread((t) => [
        ...t,
        { role: 'audrey', text: data.answer, citations: data.citations || [], grounded: data.grounded !== false },
      ]);
    } catch (e) {
      if (mine !== convo.current) return;
      // A spend-gate or permission denial is already toasted by the api layer.
      setError(errorMessage(e, 'Audrey could not answer. Try again in a minute.'));
    } finally {
      if (mine === convo.current) setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(question);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className || 'inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 text-slate-700 text-sm hover:bg-slate-50'}
        data-testid="ask-audrey-btn"
      >
        <MessageCircleQuestion size={14} /> Ask Audrey
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className="p-0 w-full max-w-md sm:max-w-md border-0 shadow-2xl bg-white flex flex-col"
          data-testid="ask-audrey-pane"
        >
          <div className="px-6 py-4 border-b border-slate-100">
            <SheetTitle className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Ask Audrey</SheetTitle>
            <div className="text-xs text-slate-500 mt-0.5">
              Answers come from what is on file for this {kind === 'deal' ? 'deal' : 'contact'}.
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="ask-audrey-thread">
            {thread.length === 0 && !loading && (
              <div className="space-y-2" data-testid="ask-audrey-starters">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Try asking</div>
                {STARTERS[kind === 'deal' ? 'deal' : 'contact'].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                    data-testid="ask-audrey-starter"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {thread.map((m, i) =>
              m.role === 'user' ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-slate-100 px-3 py-2 text-sm text-slate-800" data-testid="ask-audrey-question">
                    {m.text}
                  </div>
                </div>
              ) : (
                <div key={i} className="space-y-2" data-testid="ask-audrey-answer">
                  <div className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="ask-audrey-answer-text">{m.text}</div>
                  {m.citations.length > 0 && (
                    <div className="text-xs text-slate-500 space-y-0.5" data-testid="ask-audrey-sources">
                      <div className="uppercase tracking-widest font-bold text-[10px]">Sources</div>
                      {m.citations.map((c) => (
                        <div key={c.n} data-testid="ask-audrey-source">[{c.n}] {c.label}</div>
                      ))}
                    </div>
                  )}
                  {!m.grounded && (
                    <div className="text-xs text-amber-700" data-testid="ask-audrey-ungrounded">
                      Some details could not be matched to the file, so Audrey left them out.
                    </div>
                  )}
                </div>
              )
            )}
            {loading && (
              <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="ask-audrey-loading">
                <Loader2 className="animate-spin" size={16} /> Reading the file...
              </div>
            )}
            {error && <div className="text-sm text-red-600" data-testid="ask-audrey-error">{error}</div>}
            <div ref={endRef} />
          </div>

          <div className="px-6 py-3 border-t border-slate-100">
            <div className="flex items-end gap-2">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                maxLength={1000}
                placeholder="Ask about this file..."
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid="ask-audrey-input"
              />
              <button
                type="button"
                onClick={() => ask(question)}
                disabled={loading || !question.trim()}
                className="p-2.5 rounded-full text-white disabled:opacity-40"
                style={{ background: '#070B5F' }}
                aria-label="Send question"
                data-testid="ask-audrey-send"
              >
                <Send size={14} />
              </button>
            </div>
            <div className="text-[11px] text-slate-500 mt-2" data-testid="ask-audrey-note">
              AI-generated from what is on file. Review it before relying on it. Never type a Social Security number.
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
